import { useEffect, useState, useRef } from "react"
import { HocuspocusProvider } from "@hocuspocus/provider"
import * as Y from "yjs"
import { jwtTokenManager } from "../auth/jwt-token-manager"
import { Sentry } from "../../layout/sentry"
import { setOnlineUsers, setIsConnected, setSessionId } from "./presence-store"

const MAX_AUTHENTICATION_RETRIES = 2

/**
 * React hook for real-time user presence tracking across multiple browser tabs.
 *
 * This hook establishes a WebSocket connection to track users currently active
 * within the same account, including which pages they're viewing and their tab status.
 * It automatically handles tab lifecycle events (focus, blur, close) and merges
 * presence data from multiple tabs per user.
 *
 * @param {Object} user - Current user object with id, name, email, avatar_url, account_id
 * @returns {Object} Presence state and methods
 * @returns {string} returns.sessionId - Unique identifier for current browser session
 * @returns {HocuspocusProvider|null} returns.provider - WebSocket provider instance
 *
 * @example
 * const { provider, sessionId } = usePresence(currentUser)
 */
export function usePresence(user) {
  const retryCount = useRef(0)
  const [provider, setProvider] = useState(null)
  const [sessionId] = useState(() => {
    const storageKey = "presence-session-id"
    let id = sessionStorage.getItem(storageKey)
    if (!id) {
      id = `session-${Date.now()}-${Math.random()}`
      sessionStorage.setItem(storageKey, id)
    }
    return id
  })
  const accountId = user?.account_id

  useEffect(() => {
    setSessionId(sessionId)
  }, [sessionId])

  const url = `${process.env.NODE_FUNCTIONS_WEBSOCKET_HOST_WSS}/presence?sessionId=${sessionId}`

  useEffect(() => {
    if (!user || !user.account_id) return

    const setupPresence = async () => {
      try {
        const token = await jwtTokenManager.getValidToken()

        const doc = new Y.Doc()
        // Initialize the pageUpdates map for tracking page version counters
        doc.getMap("pageUpdates")

        const presenceProvider = new HocuspocusProvider({
          url,
          name: `presence:account-${user.account_id}`,
          document: doc,
          token: token
        })

        presenceProvider.on("authenticationFailed", async data => {
          console.warn(
            "Websocket authentication failed, attempting token refresh:",
            data
          )

          if (retryCount.current >= MAX_AUTHENTICATION_RETRIES) {
            console.error(
              `Max retries (${MAX_AUTHENTICATION_RETRIES}) reached for authentication`
            )
            reportError(
              new Error(
                `WebSocket auth failed after ${MAX_AUTHENTICATION_RETRIES} retries`
              )
            )

            presenceProvider.disconnect()
            return
          }

          try {
            // Force a reconnect by disconnecting and connecting after token is refreshed
            presenceProvider.disconnect()

            retryCount.current = retryCount.current + 1
            const newToken = await jwtTokenManager.refreshToken()
            presenceProvider.configuration.token = newToken

            await new Promise(resolve =>
              setTimeout(resolve, 1000 * retryCount.current)
            )

            presenceProvider.connect()
          } catch (error) {
            reportError(error)
            console.error("Failed to refresh websocket token:", error)
          }
        })

        presenceProvider.on("authenticated", () => {
          retryCount.current = 0
        })

        presenceProvider.on("connect", () => {
          setIsConnected(true)
        })

        presenceProvider.on("disconnect", () => {
          setIsConnected(false)
        })

        const cleanupFunctions = setupPresenceProvider(
          presenceProvider,
          user,
          sessionId,
          setProvider
        )

        return { presenceProvider, cleanupFunctions }
      } catch (error) {
        reportError(error)
        console.error("Failed to setup websocket presence:", error)
      }
    }

    let cleanup = null

    setupPresence().then(result => {
      if (result) {
        cleanup = () => {
          const { presenceProvider, cleanupFunctions } = result
          cleanupPresenceProvider(presenceProvider, sessionId, cleanupFunctions)
        }
      }
    })

    return () => {
      if (cleanup) {
        cleanup()
      }
      // Clear sessionId from store on unmount
      setSessionId(null)
    }
  }, [user, accountId, url, sessionId])

  return { provider, sessionId }
}

function setupPresenceProvider(presenceProvider, user, sessionId, setProvider) {
  // Set user info (same across all tabs)
  presenceProvider.awareness.setLocalStateField("user", {
    id: user.id,
    name: user.name,
    email: user.email,
    avatarUrl: user.avatar_url,
    accountId: user.account_id
  })

  // Set tab-specific activity
  presenceProvider.awareness.setLocalStateField("tabs", {
    [sessionId]: {
      page: window.location.pathname,
      timestamp: Date.now(),
      status: "active",
      lastSeen: Date.now(),
      title: document.title,
      isActive: !document.hidden // Track if tab is focused
    }
  })

  // Listen for presence changes and merge tabs per user
  const updateOnlineUsers = () => {
    const states = Array.from(presenceProvider.awareness.getStates().values())

    // Group by user ID to merge multiple tabs
    const userMap = new Map()

    states.forEach(state => {
      if (!state.user || !state.tabs) return

      const userId = state.user.id
      if (!userMap.has(userId)) {
        userMap.set(userId, {
          ...state.user,
          tabs: {}
        })
      }

      // Merge tabs from this client
      const existingUser = userMap.get(userId)
      existingUser.tabs = { ...existingUser.tabs, ...state.tabs }
    })

    setOnlineUsers(Array.from(userMap.values()))
  }

  presenceProvider.awareness.on("change", updateOnlineUsers)
  updateOnlineUsers()

  setProvider(presenceProvider)

  const updateTabState = updates => {
    const currentTabs = presenceProvider.awareness.getLocalState()?.tabs || {}
    presenceProvider.awareness.setLocalStateField("tabs", {
      ...currentTabs,
      [sessionId]: {
        ...currentTabs[sessionId],
        lastSeen: Date.now(),
        ...updates
      }
    })
  }

  // Track tab visibility changes
  const handleVisibilityChange = () => {
    updateTabState({ isActive: !document.hidden })
  }

  document.addEventListener("visibilitychange", handleVisibilityChange)

  const handlePageChange = () => {
    updateTabState({
      page: window.location.pathname,
      title: document.title
    })
  }

  document.addEventListener("inertia:success", handlePageChange)

  window.addEventListener("popstate", handlePageChange)

  // Cleanup tab when closing
  const handleBeforeUnload = () => {
    const currentTabs = presenceProvider.awareness.getLocalState()?.tabs || {}
    delete currentTabs[sessionId]
    presenceProvider.awareness.setLocalStateField("tabs", currentTabs)
  }

  window.addEventListener("beforeunload", handleBeforeUnload)

  // Store cleanup functions for later use
  return {
    updateOnlineUsers,
    handleVisibilityChange,
    handleBeforeUnload,
    handlePageChange
  }
}

function cleanupPresenceProvider(
  presenceProvider,
  sessionId,
  cleanupFunctions
) {
  if (cleanupFunctions) {
    presenceProvider.awareness.off("change", cleanupFunctions.updateOnlineUsers)
    document.removeEventListener(
      "visibilitychange",
      cleanupFunctions.handleVisibilityChange
    )
    document.removeEventListener(
      "inertia:success",
      cleanupFunctions.handlePageChange
    )
    window.removeEventListener("popstate", cleanupFunctions.handlePageChange)
    window.removeEventListener(
      "beforeunload",
      cleanupFunctions.handleBeforeUnload
    )
  }

  // Clean up this tab
  const currentTabs = presenceProvider.awareness.getLocalState()?.tabs || {}
  delete currentTabs[sessionId]
  presenceProvider.awareness.setLocalStateField("tabs", currentTabs)
  presenceProvider.destroy()

  // Reset connection state
  setIsConnected(false)
}

function reportError(error) {
  Sentry.withScope(scope => {
    scope.setTag("context", "usePresence")
    scope.setLevel("warning")
    Sentry.captureException(error)
  })
}
